import React, { useState } from "react";
import { Card, Upload, Button, Typography } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import Papa from "papaparse";
import fileApi from "../../api/fileApi";

const { Dragger } = Upload;
const { Text } = Typography;

// ✅ Sample headers (your required format)
const SAMPLE_HEADERS = [
    "First Name","last Name","title","function","level","email","phone","linkedin",
    "address","city","state","postal_code","country",
    "company_name","company_domain","company_phone","company_industry",
    "company_sub_industry","company_employee_size","company_revenue",
    "company_linkedin","company_technologies",
    "company_address","company_city","company_state",
    "company_postal_code","company_country",
    "company_sic_code","company_naic_code"
];

const FileUploadPage = () => {
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);

    const allowedTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ];

    // ✅ Download sample CSV
    const downloadSampleFile = () => {
        const csvContent =
            SAMPLE_HEADERS.join(",") + "\n" +
            SAMPLE_HEADERS.map(() => "").join(",");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "sample_leads_file.csv";
        link.click();
    };

    // ✅ Validate CSV headers
    const validateFileStructure = (file) => {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                preview: 1,
                skipEmptyLines: true,
                complete: (result) => {
                    const headers = result.data[0];

                    if (!headers) {
                        toast.error("File is empty or invalid");
                        return reject(false);
                    }

                    const normalizedHeaders = headers.map(h => h?.trim());

                    const isValid =
                        normalizedHeaders.length === SAMPLE_HEADERS.length &&
                        normalizedHeaders.every((h, i) => h === SAMPLE_HEADERS[i]);

                    if (!isValid) {
                        toast.error("Invalid file format. Please download and use the sample file.");
                        return reject(false);
                    }

                    resolve(true);
                },
                error: () => {
                    toast.error("Error reading file");
                    reject(false);
                }
            });
        });
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            toast.warning("Please select a file first");
            return;
        }

        setUploading(true);

        try {
            const file = fileList[0];
            const res = await fileApi.uploadFile(file);

            if (res.success) {
                toast.success("File uploaded successfully. Processing started.");
                setFileList([]);
            } else {
                toast.error(res.message || "Upload failed");
            }
        } catch (error) {
            toast.error("Something went wrong during upload");
        }

        setUploading(false);
    };

    const uploadProps = {
        multiple: false,
        accept: ".csv,.xls,.xlsx",
        fileList,

        beforeUpload: async (file) => {
            if (!allowedTypes.includes(file.type)) {
                toast.error("Only CSV and Excel files are allowed");
                return Upload.LIST_IGNORE;
            }

            try {
                // ✅ Only validate CSV (Excel validation needs xlsx lib)
                if (file.type === "text/csv") {
                    await validateFileStructure(file);
                }

                setFileList([file]);
            } catch {
                return Upload.LIST_IGNORE;
            }

            return false;
        },

        onRemove: () => {
            setFileList([]);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: 80
            }}
        >
            <Card
                title="Upload B2B Data File"
                style={{
                    width: 600,
                    borderRadius: 10,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)"
                }}
            >
                <Dragger {...uploadProps} style={{ padding: 20 }}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: "var(--highlight-color)" }} />
                    </p>

                    <p className="ant-upload-text">
                        Click or drag file to upload
                    </p>

                    <Text type="secondary">
                        Supported formats: CSV, XLS, XLSX
                    </Text>
                </Dragger>

                {/* ✅ Sample Format Display */}
                <div style={{ marginTop: 20 }}>
                    <Text strong>Expected File Format:</Text>

                    <div
                        style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: "#666",
                            background: "#fafafa",
                            padding: 10,
                            borderRadius: 6,
                            maxHeight: 120,
                            overflow: "auto"
                        }}
                    >
                        {SAMPLE_HEADERS.join(" | ")}
                    </div>

                    <Button
                        type="link"
                        onClick={downloadSampleFile}
                        style={{ padding: 0, marginTop: 8 }}
                    >
                        Download Sample File
                    </Button>
                </div>

                <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleUpload}
                    loading={uploading}
                    block
                    style={{
                        marginTop: 20,
                        background: "var(--button-bg)",
                        borderColor: "var(--button-bg)"
                    }}
                >
                    Upload File
                </Button>
            </Card>
        </div>
    );
};

export default FileUploadPage;